<template>
  <DashboardWidgetCard
    title="Opportunities"
    subtitle="Pipeline for the selected period"
    icon="mdi:briefcase-outline"
    accent="#10b981"
    :link-to="{ name: OPPORTUNITIES_REPORTS_ROUTE }"
    link-label="Full report"
    :is-loading="isLoading"
  >
    <div class="opp-kpis">
      <div v-for="kpi in kpis" :key="kpi.key" class="opp-kpi">
        <div class="opp-kpi-label">{{ kpi.label }}</div>
        <div class="opp-kpi-value">{{ kpi.value }}</div>
        <div
          v-if="kpi.change !== null"
          class="opp-kpi-change"
          :class="kpi.change >= 0 ? 'opp-kpi-change--up' : 'opp-kpi-change--down'"
        >
          <Icon :icon="kpi.change >= 0 ? 'mdi:trending-up' : 'mdi:trending-down'" width="14" />
          {{ Math.abs(kpi.change).toFixed(1) }}%
        </div>
      </div>
    </div>

    <div class="opp-stages">
      <div class="opp-stages-heading">
        <span>Pipeline by stage</span>
        <span>{{ totalDeals }} deals</span>
      </div>

      <div v-if="stages.length === 0" class="opp-empty">
        <Icon icon="mdi:chart-timeline-variant" width="28" height="28" />
        <span>No opportunities in this period</span>
      </div>

      <ul v-else class="opp-stage-list">
        <li v-for="stage in stages" :key="stage.name" class="opp-stage">
          <div class="opp-stage-row">
            <span class="opp-stage-name">
              <span class="opp-stage-dot" :style="{ background: stage.color }" />
              {{ stage.name }}
            </span>
            <span class="opp-stage-figures">
              <strong>{{ stage.count }}</strong>
              <span class="opp-stage-value">{{ formatCurrency(stage.value) }}</span>
            </span>
          </div>
          <div class="opp-stage-track">
            <span
              class="opp-stage-bar"
              :style="{ width: `${stage.width}%`, background: stage.color }"
            />
          </div>
        </li>
      </ul>
    </div>
  </DashboardWidgetCard>
</template>

<script setup>
import { computed } from "vue";
import { Icon } from "@iconify/vue";
import DashboardWidgetCard from "./DashboardWidgetCard.vue";
import { useOpportunityDashboard } from "@/composables/reports";
import { OPPORTUNITIES_REPORTS_ROUTE } from "@/router/routes";

defineOptions({
  name: "DashboardOpportunitiesWidget",
});

const props = defineProps({
  organizationId: { type: String, default: null },
  startDate: { type: String, default: null },
  endDate: { type: String, default: null },
  /** Owner to scope opportunities to. Empty means all owners. */
  ownerId: { type: String, default: null },
});

const STAGE_PALETTE = ["#6366f1", "#8b5cf6", "#0ea5e9", "#14b8a6", "#f59e0b", "#ec4899"];

const params = computed(() => {
  if (!props.organizationId) return null;
  return {
    organizationId: props.organizationId,
    ...(props.startDate && { startDate: props.startDate }),
    ...(props.endDate && { endDate: props.endDate }),
    ...(props.ownerId && { ownerIds: [props.ownerId] }),
  };
});

const { data, isLoading } = useOpportunityDashboard(params);

const changeOrNull = (value) => (typeof value === "number" && value !== 0 ? value : null);

const kpis = computed(() => {
  const d = data.value || {};
  return [
    {
      key: "pipeline",
      label: "Pipeline (weighted)",
      value: formatCurrency(d.totalPipelineValue, true),
      change: changeOrNull(d.totalPipelineValueChangePercent),
    },
    {
      key: "won",
      label: "Closed won",
      value: formatCurrency(d.closedWonRevenue, true),
      change: changeOrNull(d.closedWonRevenueChangePercent),
    },
    {
      key: "winRate",
      label: "Win rate",
      value: `${(d.winRate || 0).toFixed(1)}%`,
      change: changeOrNull(d.winRateChangePercent),
    },
    {
      key: "count",
      label: "Opportunities",
      value: new Intl.NumberFormat("en-US").format(d.opportunities || 0),
      change: changeOrNull(d.opportunitiesChangePercent),
    },
  ];
});

const stages = computed(() => {
  const funnel = Array.isArray(data.value?.salesFunnel) ? data.value.salesFunnel : [];
  const maxCount = Math.max(1, ...funnel.map((s) => s.count || 0));
  let paletteIndex = 0;

  return funnel.map((stage) => {
    const name = stage.stageName || stage.label || "Stage";
    let color;
    if (name === "Closed Won") color = "#10b981";
    else if (name === "Closed Lost") color = "#ef4444";
    else color = STAGE_PALETTE[paletteIndex++ % STAGE_PALETTE.length];

    const count = stage.count || 0;
    return {
      name,
      count,
      value: stage.totalValue || 0,
      color,
      // Keep a sliver visible for empty stages so the list still reads as a chart.
      width: count > 0 ? Math.max(4, (count / maxCount) * 100) : 0,
    };
  });
});

const totalDeals = computed(() => stages.value.reduce((sum, s) => sum + s.count, 0));

function formatCurrency(value, compact = false) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 0,
  }).format(value || 0);
}
</script>

<style scoped>
.opp-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.opp-kpi {
  padding: 12px 14px;
  border-radius: 12px;
  background: #fafafc;
  border: 1px solid #f0f0f5;
  min-width: 0;
}

.opp-kpi-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.opp-kpi-value {
  margin-top: 4px;
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.15;
}

.opp-kpi-change {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 4px;
  font-size: 0.72rem;
  font-weight: 600;
}

.opp-kpi-change--up {
  color: #059669;
}

.opp-kpi-change--down {
  color: #dc2626;
}

.opp-stages {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f5;
}

.opp-stages-heading {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 12px;
}

.opp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 0;
  color: #9ca3af;
  font-size: 0.85rem;
}

.opp-stage-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.opp-stage-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 5px;
}

.opp-stage-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.opp-stage-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.opp-stage-figures {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 0.85rem;
  color: #111827;
  flex-shrink: 0;
}

.opp-stage-value {
  font-size: 0.76rem;
  color: #6b7280;
  min-width: 64px;
  text-align: right;
}

.opp-stage-track {
  height: 6px;
  border-radius: 999px;
  background: #f3f4f6;
  overflow: hidden;
}

.opp-stage-bar {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

@media (max-width: 1400px) {
  .opp-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
